import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { AccessLinkForm } from "@/components/portal/AccessLinkForm";
import { toast } from "sonner";

/**
 * /hub-invite/:token — public page. Reads the invite metadata, lets the
 * invitee set a password + name, creates their user account, logs them in,
 * and forwards to their portal (/lender or /refer).
 */
export default function HubInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: "", password: "", confirm: "" });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/hub-invite/${token}`);
        setMeta(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invite not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8)             { toast.error("Password must be 8+ characters"); return; }
    if (form.password !== form.confirm)       { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post(`/hub-invite/${token}/claim`, { password: form.password, first_name: form.first_name || null });
      // Log the user in with the credentials they just set
      const res = await login(meta.email, form.password);
      if (!res.ok) {
        toast.error(res.error || "Signed up — please log in manually");
        nav("/login", { replace: true });
        return;
      }
      toast.success("Welcome — you're in.");
      const role = res.user?.role;
      nav(role === "lender" ? "/lender" : "/introducer", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#070c1a] text-white p-4 relative overflow-hidden" data-testid="hub-invite-page">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <div className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-[#0055FF]/[0.18] blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full bg-emerald-500/[0.08] blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
            <ShieldCheck className="size-3" /> Connexis · Private invite
          </div>
          <h1 className="mt-3 text-3xl tracking-[-0.02em] font-semibold">
            {loading ? "Checking your invite…" : meta ? "Set up your account" : "Invite unavailable"}
          </h1>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl p-6 shadow-[0_30px_80px_-30px_rgba(0,85,255,0.4)]">
          {loading ? (
            <div className="grid place-items-center py-8"><Loader2 className="size-6 animate-spin text-white/50" /></div>
          ) : error ? (
            <div className="py-1" data-testid="invite-error">
              <p className="text-[14px] text-white/70 leading-relaxed text-center">{error}</p>
              <p className="mt-2 text-[12.5px] text-white/40 text-center">
                No problem — pop your email in and we'll send you a fresh link straight away.
              </p>
              <AccessLinkForm testid="invite-recover" />
            </div>
          ) : (
            <>
              <div className="space-y-1 mb-5">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/40 font-semibold">Signing in as</div>
                <div className="text-[15px] font-semibold text-white">{meta.name}</div>
                <div className="text-[12.5px] text-white/50">{meta.email}</div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/40 mt-2">
                  Portal: <span className="text-[#7da9ff]">{meta.kind === "lender" ? "Lender / Funder" : meta.kind === "seat" ? `Your seat at ${meta.company || "the company"}` : "Referral partner"}</span>
                </div>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <F label="First name (optional)"><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="bg-white/[0.04] border-white/10 text-white h-11" data-testid="invite-first-name" /></F>
                <F label="Set a password *"><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="bg-white/[0.04] border-white/10 text-white h-11" data-testid="invite-password" /></F>
                <F label="Confirm password *"><Input type="password" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} className="bg-white/[0.04] border-white/10 text-white h-11" data-testid="invite-confirm" /></F>
                <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-gradient-to-r from-[#0055FF] to-[#3d7bff] hover:from-[#0044CC] hover:to-[#0055FF] font-semibold shadow-[0_12px_32px_rgba(0,85,255,0.5)]" data-testid="invite-claim">
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <>Activate account <ArrowRight className="size-3.5 ml-1" /></>}
                </Button>
                <p className="text-[10.5px] text-white/40 text-center leading-relaxed">
                  By activating you agree to our terms and privacy policy. Your account is private and never appears on the public site.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
